/* eslint-disable max-lines */
import TutorialBackground from '@/assets/image/tutorial/background.png';
import CircleCheckIcon from '@/components/ui/circle-check-icon';
import GreyCircleIcon from '@/components/ui/grey-circle-icon';
import {
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
  IS_GAMIFY_TRIAL_LENGTH_ENABLED,
  IS_POST_NOTE_QUALITY_PANEL_ENABLED,
} from '@/features/flags/legacy-flags';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import {
  type Page,
  stepsMapping,
  tutorialVisibleAtom,
  useTutorial,
} from '@/hooks/useTutorial';
import {
  trackGettingStartedPanelClosed,
  trackGettingStartedPanelOpened,
} from '@/lib/analytics/scribe/getting-started';
import { cn } from '@/lib/cn';
import { TemplatePages, type TutorialSteps } from '@/types/schema';
import { isOnPrem } from '@/utils/buildProfile';
import { badgeVariants } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { CompletionPieChart } from '@heidi/design-system/completion-pie-chart';
import { MiniCompletionPieChart } from '@heidi/design-system/mini-completion-pie-chart';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyH4,
  TypographyP2,
  TypographyP2Bold,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom } from 'jotai';
import { ChevronRight, Maximize2, X } from 'lucide-react';
import * as m from 'motion/react-m';
import Image from 'next/image';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ReferralSection } from './referral-section';

interface TutorialStepPage {
  title: string;
  description: string;
  image?: string;
  enum: Page;
  order: number;
  total: number;
}

interface TutorialStepData {
  title: string;
  completed: boolean;
  open: boolean;
  enum: TutorialSteps;
}

interface TutorialStepItemProps {
  step: TutorialStepData;
  isActive: boolean;
  newlyCompleted: boolean;
  onClick: () => void;
  showDivider: boolean;
}

interface TutorialStepFooterProps {
  onBack: () => void;
  onNext: () => void;
  tutorialStepCurrentPage?: TutorialStepPage;
}

interface TutorialChecklistStepsProps {
  userName: string;
  steps: TutorialStepData[];
  activeTab?: TutorialSteps;
  isNewlyCompletedThisSession: (step: TutorialStepData) => boolean;
  onStepClick: (params: { step: TutorialSteps; page: Page }) => void;
  onHide: () => void;
  hasCompletedAllSteps: boolean;
  completionPercentage: number;
}

interface TutorialStepViewProps {
  tutorialStepCurrentPage?: TutorialStepPage;
  onBack: () => void;
  onNext: () => void;
}

const TutorialStepItem = memo(
  ({ step, isActive, newlyCompleted, onClick, showDivider }: TutorialStepItemProps) => {
    const [showCheck, setShowCheck] = useState(!newlyCompleted);

    useEffect(() => {
      if (newlyCompleted) {
        const timer = setTimeout(() => {
          setShowCheck(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    }, [newlyCompleted]);

    return (
      <m.div
        key={step.enum}
        {...(newlyCompleted && showCheck
          ? {
              initial: { opacity: 0, scale: 0.8 },
              animate: { opacity: 1, scale: 1 },
              transition: { duration: 0.3, delay: 0.5 },
            }
          : {})}
        onClick={onClick}
        className={cn(
          'relative flex cursor-pointer items-center justify-between rounded-md p-2',
          'overflow-visible',
          isActive && 'border border-outline bg-surface shadow-sm hover:cursor-pointer'
        )}
      >
        <div className="flex items-center gap-2">
          <div className="z-10 shrink-0 bg-surface">
            {step.completed ? (
              newlyCompleted ? (
                !showCheck ? (
                  <GreyCircleIcon />
                ) : (
                  <m.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CircleCheckIcon />
                  </m.div>
                )
              ) : (
                <CircleCheckIcon />
              )
            ) : (
              <GreyCircleIcon />
            )}
          </div>

          {isActive ? (
            <TypographyP2Bold
              className={cn(
                'overflow-visible text-text-primary',
                step.completed && 'text-text-primary'
              )}
            >
              {step.title}
            </TypographyP2Bold>
          ) : (
            <TypographyP2
              className={cn(
                'overflow-visible text-text-secondary',
                step.completed && 'text-text-primary'
              )}
            >
              {step.title}
            </TypographyP2>
          )}
        </div>

        {(isActive || step.completed) && (
          <ChevronRight
            className="ms-auto text-text-secondary rtl:-scale-x-100"
            size={16}
          />
        )}

        {showDivider && (
          <div
            className={cn(
              'absolute top-[13px] !ml-[5px] h-full w-[8px]',
              step.completed
                ? 'bg-validation-success'
                : isActive
                  ? '!ml-[4px] mt-[24px] h-2/5 bg-text-disabled'
                  : 'bg-text-disabled'
            )}
            style={{ transformOrigin: 'top' }}
          />
        )}
      </m.div>
    );
  }
);

TutorialStepItem.displayName = 'TutorialStepItem';

const TutorialStepFooter = memo(
  ({ onBack, onNext, tutorialStepCurrentPage }: TutorialStepFooterProps) => {
    const { t } = useTranslations();

    return (
      <div className="mt-1 flex w-full justify-between">
        <div className="flex gap-1 self-center">
          {[...(Array(tutorialStepCurrentPage?.total || 1) as number[])].map(
            (_, index) => (
              <div
                key={index}
                className={cn(
                  'size-2 rounded-full bg-background-secondary',
                  index + 1 === tutorialStepCurrentPage?.order && 'bg-[#0EA5E9]'
                )}
              ></div>
            )
          )}
        </div>
        <div className="flex items-center">
          <button
            className="flex items-center justify-center px-2.5 py-1.5 text-text-secondary"
            onClick={onBack}
          >
            <TypographyCaption>{t('demo.back')}</TypographyCaption>
          </button>

          <button
            className="flex items-center justify-center rounded-md bg-background-secondary px-2.5 py-1.5 text-text-primary hover:bg-background-secondary-hover"
            onClick={onNext}
          >
            <TypographyCaptionBold>{t('demo.next')}</TypographyCaptionBold>
          </button>
        </div>
      </div>
    );
  }
);

TutorialStepFooter.displayName = 'TutorialStepper';

const TutorialChecklistSteps = memo(
  ({
    steps,
    activeTab,
    userName,
    isNewlyCompletedThisSession,
    onStepClick,
    hasCompletedAllSteps,
    completionPercentage,
  }: TutorialChecklistStepsProps) => {
    const { t } = useTranslations();

    return (
      <m.div
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex h-fit w-[318px] max-w-md flex-col overflow-hidden rounded-lg border border-outline bg-surface shadow-md"
      >
        <div className="relative h-[105px] w-full overflow-hidden">
          <Image
            src={TutorialBackground}
            alt="background"
            fill
            className="object-cover"
          />

          <div className="absolute inset-0 z-10 flex flex-row items-center gap-5 p-5">
            <CompletionPieChart
              percentage={completionPercentage}
              className="min-w-fit flex-1"
            />
            <section className="flex flex-col">
              <TypographyH4 className="text-text-primary-invert">
                {hasCompletedAllSteps
                  ? `${t('tutorial.youDidIt')} ${userName}!`
                  : `${t('tutorial.keepItUp')} ${userName}!`}
              </TypographyH4>
              <TypographyCaption className={cn('text-text-primary-invert')}>
                {hasCompletedAllSteps
                  ? t('tutorial.basicsCompleted')
                  : t('tutorial.completeTutorial')}
              </TypographyCaption>
            </section>
          </div>
        </div>

        <div className="p-4">
          {steps.map((step, index) => {
            const isActive = step.enum === activeTab;
            const newlyCompleted = isNewlyCompletedThisSession(step);

            return (
              <TutorialStepItem
                key={step.enum}
                step={step}
                isActive={isActive}
                newlyCompleted={newlyCompleted}
                onClick={() => {
                  if (hasCompletedAllSteps) {
                    // Freed up to review any step
                    const firstPage = stepsMapping[step.enum]?.steps?.[0]?.enum;
                    if (firstPage) onStepClick({ step: step.enum, page: firstPage });
                  } else {
                    if (isActive) {
                      const stepToGo = stepsMapping[step.enum]?.steps?.at(0)?.enum;
                      if (!stepToGo) return;
                      onStepClick({ step: step.enum, page: stepToGo });
                    }
                  }
                }}
                showDivider={index < steps.length - 1}
              />
            );
          })}
          {hasCompletedAllSteps && <ReferralSection />}
        </div>
      </m.div>
    );
  }
);

TutorialChecklistSteps.displayName = 'TutorialChecklistSteps';

const TutorialStepView = memo(
  ({ tutorialStepCurrentPage, onBack, onNext }: TutorialStepViewProps) => {
    return (
      <m.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.3 }}
        className="flex h-fit w-[298px] max-w-md flex-col gap-3 rounded-lg border border-outline bg-surface px-5 py-4 shadow-md"
      >
        <div className="flex flex-col">
          <TypographyPBold>{tutorialStepCurrentPage?.title}</TypographyPBold>
          <TypographyCaption className="text-text-secondary">
            {tutorialStepCurrentPage?.description}
          </TypographyCaption>
        </div>
        <div className="flex flex-col gap-2 overflow-hidden rounded-md border border-outline">
          {tutorialStepCurrentPage?.image ? (
            <video
              src={tutorialStepCurrentPage?.image}
              style={{ height: 'auto', width: '640px' }}
              loop
              muted
              autoPlay
            />
          ) : (
            <div className="flex h-[200px] w-full items-center justify-center bg-text-disabled">
              No image
            </div>
          )}
        </div>
        <TutorialStepFooter
          onBack={onBack}
          onNext={onNext}
          tutorialStepCurrentPage={tutorialStepCurrentPage}
        />
      </m.div>
    );
  }
);

TutorialStepView.displayName = 'TutorialStepView';

const TutorialChecklist = () => {
  const {
    panelOpen,
    setPanelOpen,
    tutorialStep,
    setTutorialStep,
    steps,
    nextUncompletedStep,
    recentlyCompletedSteps,
    clearRecentlyCompletedSteps,
    tutorialStepCurrentPage,
    onNextPage,
    onBackPage,
    hasCompletedAllSteps,
    completionPercentage,
    userName,
    showPercentageChart,
  } = useTutorial();
  const [tutorialVisible] = useAtom(tutorialVisibleAtom);
  const { isAuthenticated } = useIsAuthenticated();
  const { t } = useTranslations();
  const { data: user } = useQueryUser((data) => ({
    onboardingTaskCompletedAt: data?.onboarding_tasks_completed_at,
  }));

  // Auto-hide tutorials 7 days after completing all steps
  const shouldAutoHide = useMemo(() => {
    if (!user?.onboardingTaskCompletedAt) {
      return false;
    }
    const completedDate = new Date(user.onboardingTaskCompletedAt);
    const daysSinceCompletion = Math.floor(
      (Date.now() - completedDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysSinceCompletion >= 7;
  }, [user?.onboardingTaskCompletedAt]);

  const handleStepClick = useCallback(
    (step: TutorialSteps, page: Page) => {
      setTutorialStep({ step, page });
      clearRecentlyCompletedSteps();
    },
    [setTutorialStep, clearRecentlyCompletedSteps]
  );

  const isNewlyCompletedThisSession = useCallback(
    (step: TutorialStepData) => {
      return step.completed && recentlyCompletedSteps.includes(step.enum);
    },
    [recentlyCompletedSteps]
  );

  const activeTab = nextUncompletedStep;

  const currentView = useMemo(() => {
    if (tutorialStep?.step) {
      return (
        <TutorialStepView
          tutorialStepCurrentPage={tutorialStepCurrentPage}
          onBack={onBackPage}
          onNext={onNextPage}
        />
      );
    }
    return (
      <TutorialChecklistSteps
        steps={steps}
        userName={userName}
        activeTab={activeTab}
        isNewlyCompletedThisSession={isNewlyCompletedThisSession}
        hasCompletedAllSteps={hasCompletedAllSteps}
        completionPercentage={completionPercentage}
        onStepClick={(params) => handleStepClick(params.step, params.page)}
        onHide={() => setPanelOpen(false)}
      />
    );
  }, [
    tutorialStep,
    tutorialStepCurrentPage,
    onBackPage,
    onNextPage,
    steps,
    userName,
    activeTab,
    isNewlyCompletedThisSession,
    hasCompletedAllSteps,
    completionPercentage,
    handleStepClick,
    setPanelOpen,
  ]);

  if (
    // Tutorial content (videos, referral program) assumes the cloud product —
    // hide the whole surface on airgapped on-prem deployments.
    isOnPrem ||
    !isAuthenticated ||
    !tutorialVisible ||
    IS_GAMIFY_TRIAL_LENGTH_ENABLED() ||
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() ||
    // GRO-3268: the post-note quality panel replaces the tutorial spotlight as
    // the in-session coaching surface for users in that experience.
    IS_POST_NOTE_QUALITY_PANEL_ENABLED() ||
    shouldAutoHide
  ) {
    return null;
  }

  return (
    <Popover
      open={panelOpen}
      onOpenChange={(open) => {
        if (open) {
          trackGettingStartedPanelOpened();
        } else {
          trackGettingStartedPanelClosed();
        }
        setPanelOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={cn(
            'absolute bottom-3 right-8 z-10 cursor-pointer',
            badgeVariants({
              color: 'neutral',
              size: 'sm',
              className: 'bg-surface',
            })
          )}
        >
          <span> {t('tutorials.tutorials')} </span>{' '}
          {showPercentageChart ? (
            <div className="flex items-center gap-1">
              <span>{completionPercentage}%</span>
              <MiniCompletionPieChart percentage={completionPercentage} />
            </div>
          ) : (
            <Maximize2 size={10} />
          )}
        </m.div>
      </PopoverTrigger>

      <PopoverContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        sideOffset={-26}
        alignOffset={-8}
        side="top"
        align="end"
        className={cn(
          'flex h-fit w-[298px] justify-end overflow-hidden rounded-md border-none p-0',
          tutorialStepCurrentPage?.enum === TemplatePages.SET_AS_DEFAULT_TEMPLATE
            ? '!z-10'
            : '!z-[101]',
          'relative'
        )}
      >
        <Button
          variant="ghost"
          size="xxs"
          iconOnly
          className={cn('absolute end-2 top-2 z-20')}
          onClick={() => setPanelOpen(false)}
          aria-label={t('common.close')}
        >
          <X />
        </Button>

        {currentView}
      </PopoverContent>
    </Popover>
  );
};

export default TutorialChecklist;
